/**
 * Global session-expiry state.
 *
 * When ANY API call returns 401, the apiRequest interceptor calls
 * `triggerSessionExpired()` which flips a flag and notifies every
 * subscriber. The top-level <SessionExpiredModal> in App.tsx listens
 * and renders a blocking overlay — no per-component error handling
 * needed for expired tokens.
 *
 * The modal is deliberately NOT dismissible (no "X" button). The only
 * action is "Sign In Again" which clears localStorage and redirects
 * to the login page. This prevents the user from continuing to interact
 * with stale data behind a broken auth session.
 */

type Listener = () => void;

let expired = false;
const listeners = new Set<Listener>();

/** Called by apiRequest when it receives a 401. Safe to call multiple
 *  times — only the first call fires listeners; subsequent ones are
 *  no-ops so the modal doesn't re-render on every failed request. */
export function triggerSessionExpired(): void {
    if (expired) return; // already triggered, don't re-fire
    expired = true;
    listeners.forEach((fn) => fn());
}

/** Returns true if the session has been marked as expired. */
export function isSessionExpired(): boolean {
    return expired;
}

/** Reset the flag (called after the user clicks "Sign In Again" and
 *  localStorage is cleared, so the next login starts fresh). */
export function resetSessionExpired(): void {
    expired = false;
}

/** Subscribe to the session-expired event. Returns an unsubscribe fn. */
export function onSessionExpired(fn: Listener): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
}
